import React, {
    useState,
    useRef,
    useCallback,
    useLayoutEffect,
    useMemo
} from 'react';

const Switch = ({
    value,
    label,
    alignment,
    handler,
    small = false,
    onIcon = null,
    offIcon = null
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [dragStartX, setDragStartX] = useState(0);
    const [dragCurrentX, setDragCurrentX] = useState(0);
    const [toggleWidth, setToggleWidth] = useState(0);

    const switchRef = useRef(null);
    const toggleRef = useRef(null);

    useLayoutEffect(() => {
        if (toggleRef.current) {
            setToggleWidth(toggleRef.current.offsetWidth);
        }
    }, [small, onIcon, offIcon]);

    const maxPosition = useMemo(() => {
        if (!switchRef.current) return 0;
        return Math.max(0, switchRef.current.offsetWidth - toggleWidth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [toggleWidth, small]);

    const toggleLeft = useMemo(() => {
        if (!isDragging) {
            return value ? `${maxPosition-2}px` : '2px';
        }

        const dragDistance = dragCurrentX - dragStartX;
        const startPosition = value ? maxPosition : 0;

        const position = Math.max(
            0,
            Math.min(maxPosition, startPosition + dragDistance)
        );

        return `${position}px`;
    }, [
        isDragging,
        value,
        dragCurrentX,
        dragStartX,
        maxPosition
    ]);

    const handleDragStart = useCallback((clientX) => {
        setIsDragging(true);
        setDragStartX(clientX);
        setDragCurrentX(clientX);
    }, []);

    const handleDragMove = useCallback((clientX) => {
        if (!isDragging) return;
        setDragCurrentX(clientX);
    }, [isDragging]);

    const handleDragEnd = useCallback(() => {
        if (!isDragging) return;

        const dragDistance = dragCurrentX - dragStartX;
        const startPosition = value ? maxPosition : 0;
        const finalPosition = startPosition + dragDistance;

        const shouldBeOn = finalPosition > maxPosition / 2;

        if (shouldBeOn !== value) {
            handler();
        }

        setIsDragging(false);
    }, [
        isDragging,
        dragStartX,
        dragCurrentX,
        value,
        handler,
        maxPosition
    ]);

    return (
        <div
            className="switch-container"
            // style={{ flexDirection: alignment ? 'row' : 'row-reverse' }}
        >
            <label className="switch-label">{label}</label>
            <div
                ref={switchRef}
                className="switch"
                onClick={() => !isDragging && handler()}
                onMouseDown={(e) => handleDragStart(e.clientX)}
                onMouseMove={(e) => handleDragMove(e.clientX)}
                onMouseUp={handleDragEnd}
                onMouseLeave={handleDragEnd}
                onTouchStart={(e) => handleDragStart(e.touches[0].clientX)}
                onTouchMove={(e) => handleDragMove(e.touches[0].clientX)}
                onTouchEnd={handleDragEnd}
                style={{
                    // width: small ? '40px' : '50px',
                    backgroundColor: value ? '#5B8CCB' : '#BFBFBF',
                    position: 'relative',
                    userSelect: 'none'
                }}
            >
                <div
                    ref={toggleRef}
                    className="toggle"
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: toggleLeft,
                        transform: 'translateY(-50%)',
                        transition: isDragging ? 'none' : 'left 0.25s ease'
                    }}
                >
                    {value ? onIcon : offIcon}
                </div>
            </div>
        </div>
    );
};

export default Switch;